// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import ModifiedDropdown, {
  getAsValidRegex,
} from '@wandb/weave/common/components/elements/ModifiedDropdown';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {trackSetting} from '../../services/analytics/workspaceSettingsEvents';
import {useTrackSettingPage} from '../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {type MetricsPickerProps, MetricsRegexToggle} from './MetricsPicker';
import {
  getOrderedYAxisMetricKeys,
  getVisibleYAxisMetricKeys,
  getYAxisDropdownOptionsForKeys,
  getYAxisDropdownOptionText,
} from './MetricsPickerDropdownOptions';

const Y_AXIS_DROPDOWN_RESULT_LIMIT = 100;

export const MetricsPickerDropdown = (props: MetricsPickerProps) => {
  const {config, options, keyTypes, updateConfig} = props;
  const trackSettingPage = useTrackSettingPage();

  const [selectedValues, setSelectedValues] = useState(config.metrics);
  const orderedOptions = useMemo(
    () => getOrderedYAxisMetricKeys(options, keyTypes),
    [keyTypes, options]
  );
  const dropdownOptions = useMemo(
    () =>
      getYAxisDropdownOptionsForKeys(
        getVisibleYAxisMetricKeys(
          orderedOptions,
          selectedValues,
          Y_AXIS_DROPDOWN_RESULT_LIMIT
        ),
        keyTypes
      ),
    [keyTypes, orderedOptions, selectedValues]
  );
  const searchYAxisOptions = useCallback(
    (_options: any[], query: string) =>
      searchOrderedYAxisOptions(orderedOptions, keyTypes, query),
    [keyTypes, orderedOptions]
  );
  const firstDropdownMetric = dropdownOptions[0]?.value;
  const newMetrics = React.useMemo(
    () =>
      firstDropdownMetric == null
        ? undefined
        : getUpdatedDropdownMetrics([firstDropdownMetric], keyTypes),
    [firstDropdownMetric, keyTypes]
  );
  /**
   * Whenever we get metrics that don't match selected values, update the selected values
   * this keeps the component synced to whatever the spec is
   */
  useEffect(
    () => {
      if (!stringArraysEqual(selectedValues, config.metrics)) {
        setSelectedValues(config.metrics);
      }
    },
    // we only want to update this when a new value comes in as props
    // selected values will eagerly updated on change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.metrics]
  );

  // Set a nice default metric if possible
  useEffect(() => {
    if (selectedValues == null && newMetrics != null) {
      if (!stringArraysEqual(newMetrics, config.metrics)) {
        updateConfig({
          metrics: newMetrics,
        });
      }
    }
  }, [config.metrics, newMetrics, selectedValues, updateConfig]);

  const onDropdownChange = useCallback(
    (_, {value: newValue}) => {
      if (
        Array.isArray(newValue) &&
        newValue.every(value => typeof value === 'string')
      ) {
        const updatedMetrics = getUpdatedDropdownMetrics(newValue, keyTypes);
        setSelectedValues(updatedMetrics);
        updateConfig({
          metrics: updatedMetrics,
        });

        trackSetting({
          action: 'change',
          page: trackSettingPage,
          settingLevel: 'panel',
          setting: 'yAxis',
          settingValue: updatedMetrics.toString(),
          additionalProperties: {
            metrics: updatedMetrics,
          },
        });
      }
    },
    [keyTypes, trackSettingPage, updateConfig]
  );

  return (
    <div className="metrics-picker">
      <div className="metrics-picker-control-wrapper">
        <ModifiedDropdown
          allowRegexSearch
          className="regex-dropdown"
          data-test="y-axis"
          lazyLoad
          multiple
          onChange={onDropdownChange}
          options={dropdownOptions}
          placeholder="Select metrics to visualize"
          resultLimit={Y_AXIS_DROPDOWN_RESULT_LIMIT}
          search={searchYAxisOptions}
          selection
          value={selectedValues}
        />
        <div className="metrics-picker-dropdown-actions">
          <MetricsRegexToggle {...props} />
        </div>
      </div>
    </div>
  );
};

const getUpdatedDropdownMetrics = (
  newMetrics: string[],
  keyTypes: MetricsPickerProps['keyTypes']
) => {
  let updateConfigMetrics = newMetrics;

  // Histograms cannot be displayed alongside other metrics, including other histograms.
  // If there is a histogram present, overwrite the entire list of metrics
  // with the one just added by the user.
  // This ensures that a histogram metric will never coexist with another metric.
  if (newMetrics.some(k => keyTypes[k] === 'histogram')) {
    updateConfigMetrics = [newMetrics[newMetrics.length - 1]!];
  }
  return updateConfigMetrics;
};

function stringArraysEqual(
  left: string[] | undefined,
  right: string[] | undefined
) {
  // Metrics are flat string arrays; lodash isEqual was showing up in profiles.
  if (left === right) {
    return true;
  }
  if (left == null || right == null || left.length !== right.length) {
    return false;
  }
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) {
      return false;
    }
  }
  return true;
}

function searchOrderedYAxisOptions(
  orderedOptions: string[],
  keyTypes: MetricsPickerProps['keyTypes'],
  query: string
) {
  if (query.trim().length === 0) {
    return [];
  }

  const regex = getAsValidRegex(query);
  const queryLower = query.toLowerCase();
  const matches: string[] = [];
  const exactMatches: string[] = [];

  for (const option of orderedOptions) {
    const optionText = getYAxisDropdownOptionText(option, keyTypes);
    const matchesQuery = regex
      ? regex.test(optionText)
      : optionText.toLowerCase().includes(queryLower);

    if (!matchesQuery) {
      continue;
    }

    if (optionText.toLowerCase() === queryLower) {
      exactMatches.push(option);
    } else {
      matches.push(option);
    }
  }

  return getYAxisDropdownOptionsForKeys(
    [...exactMatches, ...matches],
    keyTypes
  );
}
